import "@/App.css";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { WagmiProvider } from "wagmi";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "./components/ui/sonner";
import { TooltipProvider } from "./components/ui/tooltip";
import { wagmiConfig } from "./lib/chains";
import { AppProvider, useApp } from "./context/AppContext";
import { Navbar, Footer } from "./components/Navbar";
import { Loading, ErrorState } from "./components/State";
import Home from "./pages/Home";
import Explore from "./pages/Explore";
import TokenProfile from "./pages/TokenProfile";
import TokenAdmin from "./pages/TokenAdmin";
import Listings from "./pages/Listings";
import Launch from "./pages/Launch";
import Liquidity from "./pages/Liquidity";
import { Leaderboard, Community } from "./pages/Social";
import Portfolio from "./pages/Portfolio";
import Admin from "./pages/Admin";
import Protocol from "./pages/Protocol";
import Profile from "./pages/Profile";
import { HoneycombBackground } from "./components/HoneycombBackground";
import { ProfileOnboarding } from "./components/ProfileOnboarding";

const NotFound = () => (
  <div className="max-w-xl mx-auto py-24 text-center" data-testid="not-found-page">
    <p className="font-display text-6xl font-black text-lime">404</p>
    <p className="mt-3 text-slate-400">This cell of the hive is empty.</p>
    <Link to="/" data-testid="not-found-home-link" className="btn-lime mt-6 px-5 py-2.5 text-sm">Back to Swap</Link>
  </div>
);

const qc = new QueryClient({ defaultOptions: { queries: { refetchOnWindowFocus: false } } });

const Shell = () => {
  const { config, configError } = useApp();
  return (
    <div className="relative min-h-screen flex flex-col">
      <HoneycombBackground />
      <ProfileOnboarding />
      <Navbar />
      <div className="flex-1">
        {configError ? <ErrorState message={`Cannot reach HIVEVORA API: ${configError}`} /> : !config ? <Loading label="Booting the hive" /> : (
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/explore" element={<Explore />} />
            <Route path="/token/:address" element={<TokenProfile />} />
            <Route path="/token/:address/admin" element={<TokenAdmin />} />
            <Route path="/listings" element={<Listings />} />
            <Route path="/launch" element={<Launch />} />
            <Route path="/liquidity" element={<Liquidity />} />
            <Route path="/leaderboard" element={<Leaderboard />} />
            <Route path="/community" element={<Community />} />
            <Route path="/portfolio" element={<Portfolio />} />
            <Route path="/admin" element={<Admin />} />
            <Route path="/protocol" element={<Protocol />} />
            <Route path="/u/:address" element={<Profile />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default function App() {
  return (
    <WagmiProvider config={wagmiConfig}>
      <QueryClientProvider client={qc}>
        <BrowserRouter>
          <AppProvider>
            <TooltipProvider delayDuration={150}>
              <Shell />
              <Toaster theme="dark" position="bottom-right" />
            </TooltipProvider>
          </AppProvider>
        </BrowserRouter>
      </QueryClientProvider>
    </WagmiProvider>
  );
}
